import React, { useEffect, useState } from "react";
import MatchCard from "./MatchCard";
import TeamLogo from "./TeamLogo";
import { KitColorSwatches, normalizeKitColors } from "./KitColorsEditor";
import { supabase } from "../supabaseClient";
import { formatGameDate, formatGameTime } from "../Functions/Formatting";
import { hexToRgba } from "../Functions/Colors";
import { matchStatus } from "../Functions/matchStatus";
import StateMessage from "./StateMessage";
import "./Equipos.css";
import "./KitColors.css";

const FRESH_MS = 60_000;

const Equipos = ({ teams = [], leagueId, themeColor, loading = false }) => {
  const [openTeamId, setOpenTeamId] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const sortedTeams = [...teams].sort((a, b) =>
    (a.team_name || "").localeCompare(b.team_name || "", "es")
  );

  useEffect(() => {
    if (!openTeamId || !leagueId) return;

    const KEY = `teamDetail:v2:${openTeamId}`;
    const cached = (() => {
      try {
        return JSON.parse(sessionStorage.getItem(KEY) || "null");
      } catch {
        return null;
      }
    })();

    const now = Date.now();

    if (cached?.data) {
      setDetailCache((prev) => ({ ...prev, [openTeamId]: cached.data }));
    }

    if (cached && now - cached.ts < FRESH_MS) return;

    let cancelled = false;

    const fetchDetail = async () => {
      setLoadingDetail(!cached?.data);
      setDetailError(null);

      try {
        const [playersRes, gamesRes] = await Promise.all([
          supabase
            .from("player_teams")
            .select(
              `
              goals,
              players!inner(player_id:id, player_name, gender)
            `
            )
            .eq("team_id", openTeamId)
            .order("goals", { ascending: false }),
          supabase
            .from("games")
            .select(
              `
              game_id:id,
              league_id,
              team1_id,
              team2_id,
              team1_score,
              team2_score,
              game_date,
              game_time,
              status,
              t1:teams!team1_id(team_name, logo_url, primary_kit_colors),
              t2:teams!team2_id(team_name, logo_url, primary_kit_colors),
              leagues!inner(id)
            `
            )
            .eq("league_id", leagueId)
            .eq("is_published", true)
            .eq("leagues.is_published", true)
            .or(`team1_id.eq.${openTeamId},team2_id.eq.${openTeamId}`)
            .order("game_date", { ascending: true })
            .order("game_time", { ascending: true }),
        ]);

        if (playersRes.error) throw playersRes.error;
        if (gamesRes.error) throw gamesRes.error;

        const players = (playersRes.data || [])
          .map((row) => ({
            player_id: row.players.player_id,
            player_name: row.players.player_name,
            gender: row.players.gender,
            goals: row.goals || 0,
          }))
          .sort((a, b) => {
            if (b.goals !== a.goals) return b.goals - a.goals;
            return (a.player_name || "").localeCompare(
              b.player_name || "",
              "es"
            );
          });

        const games = (gamesRes.data || []).map((row) => ({
          ...row,
          team1_name: row.t1?.team_name,
          team2_name: row.t2?.team_name,
          team1_logo_url: row.t1?.logo_url || null,
          team2_logo_url: row.t2?.logo_url || null,
          team1_kit_colors: row.t1?.primary_kit_colors || [],
          team2_kit_colors: row.t2?.primary_kit_colors || [],
        }));

        const data = { players, games };

        if (!cancelled) {
          setDetailCache((prev) => ({ ...prev, [openTeamId]: data }));
          sessionStorage.setItem(
            KEY,
            JSON.stringify({ data, ts: Date.now() })
          );
        }
      } catch (err) {
        console.error("Error fetching team detail:", err);
        if (!cancelled && !cached?.data) {
          setDetailError("No se pudo cargar la información del equipo.");
        }
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    };

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [openTeamId, leagueId]);

  const openDetail = openTeamId ? detailCache[openTeamId] : null;

  return (
    <section
      className="equipos"
      aria-labelledby="equipos-title"
      style={{
        "--theme-color": themeColor || "var(--accent)",
        "--theme-soft": hexToRgba(themeColor, 0.14),
      }}
    >
      <h2 className="block-title" id="equipos-title">
        Equipos
      </h2>
      <p className="equipos-subtitle">
        Selecciona un equipo para ver su plantel, uniformes y partidos.
      </p>

      {!sortedTeams.length && (
        loading ? (
          <div className="standings-skeleton" role="status" aria-label="Cargando equipos…">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className="skeleton equipo-skeleton" />
            ))}
          </div>
        ) : (
          <StateMessage title="Aún no hay equipos">
            Los equipos aparecerán aquí cuando se registren en la liga.
          </StateMessage>
        )
      )}

      <div className="equipos-list">
        {sortedTeams.map((team) => {
          const isOpen = openTeamId === team.team_id;
          return (
            <div
              key={team.team_id}
              className={`equipo-item${isOpen ? " open" : ""}`}
            >
              <button
                type="button"
                className="equipo-toggle"
                aria-expanded={isOpen}
                onClick={() =>
                  setOpenTeamId((id) =>
                    id === team.team_id ? null : team.team_id
                  )
                }
              >
                <span className="equipo-identity">
                  <TeamLogo
                    name={team.team_name}
                    logoUrl={team.logo_url}
                    size="md"
                  />
                  <span className="equipo-name">{team.team_name}</span>
                </span>
                <span className="equipo-meta">
                  {(team.games_played || 0) > 0 && (
                    <span
                      className="equipo-record"
                      title="Ganados - Empatados - Perdidos"
                    >
                      {team.wins || 0}-{team.draws || 0}-{team.losses || 0}
                    </span>
                  )}
                  {team.points != null && (
                    <span className="equipo-pts">{team.points} pts</span>
                  )}
                  <span
                    className={`equipo-chevron${isOpen ? " open" : ""}`}
                    aria-hidden
                  >
                    ▾
                  </span>
                </span>
              </button>

              {isOpen && (
                <div className="equipo-detail">
                  {loadingDetail && !openDetail && (
                    <div className="equipo-status" role="status">
                      Cargando equipo…
                    </div>
                  )}
                  {detailError && !openDetail && (
                    <div className="equipo-status error" role="alert">
                      {detailError}
                    </div>
                  )}

                  {openDetail && (
                    <>
                      {(normalizeKitColors(team.primary_kit_colors).length >
                        0 ||
                        normalizeKitColors(team.secondary_kit_colors).length >
                          0) && (
                        <div className="equipo-section">
                          <h3 className="equipo-section-title">Uniformes</h3>
                          <div className="equipo-kits">
                            {normalizeKitColors(team.primary_kit_colors)
                              .length > 0 && (
                              <div className="equipo-kit-row">
                                <span className="equipo-kit-label">Local</span>
                                <KitColorSwatches
                                  colors={team.primary_kit_colors}
                                  size={22}
                                />
                              </div>
                            )}
                            {normalizeKitColors(team.secondary_kit_colors)
                              .length > 0 && (
                              <div className="equipo-kit-row">
                                <span className="equipo-kit-label">
                                  Alterno
                                </span>
                                <KitColorSwatches
                                  colors={team.secondary_kit_colors}
                                  size={22}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="equipo-section">
                        <h3 className="equipo-section-title">Plantel</h3>
                        {openDetail.players.length === 0 ? (
                          <div className="equipo-status">
                            Sin jugadores registrados
                          </div>
                        ) : (
                          <div className="equipo-players">
                            <div className="equipo-player-row header">
                              <span>Jugador</span>
                              <span>Goles</span>
                            </div>
                            {openDetail.players.map((p) => (
                              <div
                                key={p.player_id}
                                className="equipo-player-row"
                              >
                                <span className="equipo-player-name">
                                  {p.player_name}
                                </span>
                                <span className="equipo-player-goals">
                                  {p.goals}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="equipo-section">
                        <h3 className="equipo-section-title">Partidos</h3>
                        {openDetail.games.length === 0 ? (
                          <div className="equipo-status">
                            Sin partidos publicados
                          </div>
                        ) : (
                          <div className="equipo-games">
                            {openDetail.games.map((g) => (
                              <MatchCard
                                key={g.game_id}
                                team1={g.team1_name}
                                team1Logo={g.team1_logo_url}
                                team1KitColors={g.team1_kit_colors}
                                team1Score={g.team1_score}
                                team2={g.team2_name}
                                team2Logo={g.team2_logo_url}
                                team2KitColors={g.team2_kit_colors}
                                team2Score={g.team2_score}
                                time={formatGameTime(g.game_time)}
                                date={formatGameDate(g.game_date)}
                                status={matchStatus(g)}
                                gameID={g.game_id}
                                themeColor={themeColor}
                                accentColor={resultAccentForTeam(
                                  openTeamId,
                                  g
                                )}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Equipos;

const RESULT_WIN = "#22c55e";
const RESULT_DRAW = "#eab308";
const RESULT_LOSS = "#ef4444";
const RESULT_PENDING = "var(--border-strong)";

function resultAccentForTeam(teamId, game) {
  if (!teamId || !game || matchStatus(game) !== "played") {
    return RESULT_PENDING;
  }

  const isTeam1 = String(game.team1_id) === String(teamId);
  const myScore = Number(isTeam1 ? game.team1_score : game.team2_score) || 0;
  const oppScore = Number(isTeam1 ? game.team2_score : game.team1_score) || 0;

  if (myScore > oppScore) return RESULT_WIN;
  if (myScore < oppScore) return RESULT_LOSS;
  return RESULT_DRAW;
}
